// AuditExecutor - Componente principal para ejecutar auditorías
// Sprint 3 - Mobile-first design (4.7" to desktop)

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Alert,
  CircularProgress,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
} from '@mui/icons-material';
import { useAuditState } from '../../hooks/useAuditState';
import ProgressHeader from './components/ProgressHeader';
import SectionNavigation from './components/SectionNavigation';
import QuestionRenderer from './components/QuestionRenderer';
import AutoSaveIndicator from './components/AutoSaveIndicator';

const AuditExecutor: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const auditoriaId = parseInt(id || '0', 10);
  
  // Estado para ocultar/mostrar el header del Layout al hacer scroll
  const [lastScrollY, setLastScrollY] = useState(0);
  const [hideLayoutHeader, setHideLayoutHeader] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      
      // Ocultar header del Layout cuando scroll > 100px y scrolleando hacia abajo
      if (currentScrollY > 100 && currentScrollY > lastScrollY) {
        if (!hideLayoutHeader) {
          setHideLayoutHeader(true);
        }
      } else if (currentScrollY < lastScrollY) {
        if (hideLayoutHeader) {
          setHideLayoutHeader(false);
        }
      }
      
      setLastScrollY(currentScrollY);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [lastScrollY, hideLayoutHeader]);

  // Aplicar clase al body para ocultar el header del Layout
  useEffect(() => {
    if (hideLayoutHeader) {
      document.body.classList.add('hide-layout-header');
    } else {
      document.body.classList.remove('hide-layout-header');
    }
    return () => {
      document.body.classList.remove('hide-layout-header');
    };
  }, [hideLayoutHeader]);

  const [showExitDialog, setShowExitDialog] = useState(false);

  const {
    auditoria,
    formulario,
    seccionActual,
    respuestas,
    loading,
    saving,
    error,
    isOnline,
    lastSaved,
    setRespuesta,
    setSeccionActual,
    guardarManual,
    getProgreso,
  } = useAuditState({
    auditoriaId,
    onSaveSuccess: () => {
      console.log('Guardado exitoso');
    },
    onSaveError: (err) => {
      console.error('Error guardando:', err);
    },
  });

  const progreso = getProgreso();

  // =======================
  // Handlers
  // =======================
  const handleBack = () => {
    if (respuestas.size > 0) {
      setShowExitDialog(true);
    } else {
      navigate('/audits');
    }
  };

  const handleSaveAndExit = async () => {
    await guardarManual();
    navigate('/audits');
  };

  const handleNextSection = () => {
    if (!formulario) return;
    const currentIndex = formulario.sections.findIndex((s: any) => s.id === seccionActual);
    if (currentIndex < formulario.sections.length - 1) {
      setSeccionActual(formulario.sections[currentIndex + 1].id);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handlePrevSection = () => {
    if (!formulario) return;
    const currentIndex = formulario.sections.findIndex((s: any) => s.id === seccionActual);
    if (currentIndex > 0) {
      setSeccionActual(formulario.sections[currentIndex - 1].id);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleCompleteAudit = async () => {
    // Navegar a la página de revisión final
    await guardarManual();
    navigate(`/audits/${auditoriaId}/review`);
  };

  const handleSeccionChange = (seccionId: number) => {
    setSeccionActual(seccionId);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleRespuestaChange = (
    preguntaId: number,
    categoriaId: number,
    valor: any,
    comentario?: string
  ) => {
    setRespuesta(preguntaId, categoriaId, valor, comentario);
  };

  // =======================
  // Render States
  // =======================
  if (loading) {
    return (
      <Box sx={{ px: { xs: 2, sm: 3 }, py: 4, textAlign: 'center', maxWidth: '1200px', mx: 'auto' }}>
        <CircularProgress size={60} />
        <Box sx={{ mt: 2, color: 'text.secondary' }}>
          Cargando auditoría...
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ px: { xs: 2, sm: 3 }, py: 4, maxWidth: '1200px', mx: 'auto' }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button
          variant="contained"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/audits')}
        >
          Volver a Auditorías
        </Button>
      </Box>
    );
  }

  if (!auditoria || !formulario) {
    return (
      <Box sx={{ px: { xs: 2, sm: 3 }, py: 4, maxWidth: '1200px', mx: 'auto' }}>
        <Alert severity="warning">No se encontró la auditoría</Alert>
      </Box>
    );
  }

  // Encontrar la sección actual
  const seccionActualData = formulario.sections?.find(
    (section: any) => section.id === seccionActual
  );

  if (!seccionActualData) {
    return (
      <Box sx={{ px: { xs: 2, sm: 3 }, py: 4, maxWidth: '1200px', mx: 'auto' }}>
        <Alert severity="warning">Sección no encontrada</Alert>
      </Box>
    );
  }

  const preguntas = seccionActualData.questions || [];
  const categories = formulario.categories || [];

  return (
    <Box sx={{ 
      minHeight: '100vh', 
      bgcolor: '#ffffff', // Fondo blanco
      pb: 1,
      overflowX: 'hidden', // Solo prevenir scroll horizontal
      overflowY: 'visible', // Permitir scroll vertical para que sticky funcione
      width: '100%',
      maxWidth: '100%',
      position: 'relative',
      // Compensar solo el padding del Layout en mobile (no en desktop)
      mx: 0,
      mt: -3,
      ml: { xs: 0, md: -3 }, // Solo compensar el padding del Layout en desktop
    }}>
      {/* Header con progreso */}
      <ProgressHeader
        negocioNombre={(auditoria as any).business_name || ''}
        formularioNombre={(auditoria as any).form_name || ''}
        progreso={progreso.porcentaje}
        questionsAnswered={progreso.respondidas}
        totalQuestions={progreso.total}
        onBack={handleBack}
        onSave={guardarManual}
        saving={saving}
        seccionActual={{
          nombre: seccionActualData.name,
          color: seccionActualData.color
        }}
      />

      {/* Navigation de secciones - Fixed debajo del ProgressHeader */}
      <Box 
        className="audit-section-navigation"
        sx={{ 
          position: 'fixed',
          top: { xs: 56, sm: 68 }, // CSS override controlará esto
          // En desktop, dejar espacio para el drawer (240px)
          left: { xs: 0, md: '240px' },
          right: 0,
          width: { xs: '100vw', md: 'calc(100vw - 240px)' },
          maxWidth: { xs: '100vw', md: 'calc(100vw - 240px)' },
          overflowX: 'visible',
          overflowY: 'visible',
          mx: 0,
          px: 0,
          bgcolor: 'background.paper',
          zIndex: 1100, // Por debajo del ProgressHeader (1200) pero encima del contenido
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <SectionNavigation
          sections={formulario.sections}
          seccionActual={seccionActual}
          onChange={handleSeccionChange}
          respuestas={respuestas}
        />
      </Box>

      {/* Preguntas de la categoría actual - Con padding-top para compensar headers fixed */}
      <Box sx={{ 
        px: { xs: 2, sm: 2, md: 3 }, 
        // Layout (52/64) + ProgressHeader (56/68) + CategoryNav (~58/64) = 166px / 196px
        pt: { xs: '170px', sm: '200px' },
        mt: 0,
        maxWidth: { xs: '100%', sm: '100%', md: '1200px' },
        mx: 'auto',
        width: '100%',
        overflowX: 'hidden', // Solo prevenir expansión horizontal
      }}>
        {/* Título de la sección */}
        <Box sx={{ mb: { xs: 2, sm: 2.5 }, px: { xs: 2, sm: 2 } }}>
            <Box
              sx={{
                fontSize: { xs: '1rem', sm: '1.25rem', md: '1.5rem' },
                fontWeight: 600,
                color: 'primary.main',
                mb: 0.5,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              {seccionActualData.color && (
                <Box
                  component="span"
                  sx={{
                    width: 16,
                    height: 16,
                    bgcolor: seccionActualData.color,
                    borderRadius: '50%',
                    flexShrink: 0,
                  }}
                />
              )}
              {seccionActualData.name}
            </Box>
            {seccionActualData.description && (
              <Box
                sx={{
                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                  color: 'text.secondary',
                }}
              >
                {seccionActualData.description}
              </Box>
            )}
          </Box>

          {/* Renderizar preguntas */}
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            {preguntas.map((pregunta: any, index: number) => {
              // Encontrar la categoría de esta pregunta
              const preguntaCategory = categories.find((c: any) => c.id === pregunta.category_id);
              
              return (
                <QuestionRenderer
                  key={pregunta.id}
                  pregunta={pregunta}
                  index={index}
                  categoriaId={pregunta.category_id}
                  categoryName={preguntaCategory?.nombre}
                  categoryColor={preguntaCategory?.color}
                  respuesta={respuestas.get(pregunta.id)}
                  onChange={handleRespuestaChange}
                  auditoriaId={auditoriaId}
                  evidenciasCount={0}
                  onEvidenciasUpdated={() => {
                    console.log('Evidencias actualizadas');
                  }}
                />
              );
            })}
          </Box>

          {/* Navegación entre secciones */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
              mt: { xs: 3, sm: 4 },
              pt: { xs: 2, sm: 2.5 },
              borderTop: 2,
              borderColor: 'divider',
              px: { xs: 2, sm: 2 },
              mb: { xs: 2, sm: 3 },
            }}
          >
            {/* Indicador de progreso de sección actual */}
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 1,
              px: 1,
              py: 0.75,
              bgcolor: 'info.light',
              borderRadius: 1,
              color: 'info.contrastText',
            }}>
              <Box sx={{ 
                fontSize: { xs: '0.75rem', sm: '0.8125rem' },
                fontWeight: 600,
              }}>
                Sección {formulario.sections.findIndex((s: any) => s.id === seccionActual) + 1} de {formulario.sections.length}
              </Box>
              <Box sx={{ flex: 1 }} />
              <Box sx={{ 
                fontSize: { xs: '0.7rem', sm: '0.75rem' },
              }}>
                {seccionActualData.questions.filter((p: any) => respuestas.has(p.id)).length}/{seccionActualData.questions.length} preguntas
              </Box>
            </Box>

            {/* Botones de navegación */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
              <Button
                variant="outlined"
                onClick={handlePrevSection}
                disabled={formulario.sections.findIndex((s: any) => s.id === seccionActual) === 0}
                sx={{ 
                  minWidth: { xs: 100, sm: 120 },
                  fontSize: { xs: '0.875rem', sm: '0.9375rem' },
                  py: { xs: 1, sm: 1.25 },
                  fontWeight: 600,
                }}
              >
                ← Anterior
              </Button>

              {formulario.sections.findIndex((s: any) => s.id === seccionActual) === formulario.sections.length - 1 ? (
                <Button
                  variant="contained"
                  color="success"
                  onClick={handleCompleteAudit}
                  sx={{ 
                    minWidth: { xs: 140, sm: 170 },
                    fontSize: { xs: '0.875rem', sm: '0.9375rem' },
                    py: { xs: 1, sm: 1.25 },
                    fontWeight: 700,
                    boxShadow: 3,
                    '&:hover': {
                      boxShadow: 6,
                    },
                  }}
                >
                  ✓ Revisar y Finalizar
                </Button>
              ) : (
                <Button
                  variant="contained"
                  onClick={handleNextSection}
                  sx={{ 
                    minWidth: { xs: 100, sm: 120 },
                    fontSize: { xs: '0.875rem', sm: '0.9375rem' },
                    py: { xs: 1, sm: 1.25 },
                    fontWeight: 600,
                  }}
                >
                  Siguiente →
                </Button>
              )}
            </Box>
          </Box>
      </Box>

      {/* Indicador de auto-guardado */}
      <AutoSaveIndicator saving={saving} lastSaved={lastSaved} isOnline={isOnline} />

      {/* Dialog de confirmación al salir */}
      <Dialog
        open={showExitDialog}
        onClose={() => setShowExitDialog(false)}
      >
        <DialogTitle>¿Salir de la auditoría?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Tus respuestas se guardan automáticamente. Podés retomar esta auditoría cuando quieras.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowExitDialog(false)}>
            Cancelar
          </Button>
          <Button
            onClick={handleSaveAndExit}
            variant="contained"
            startIcon={<SaveIcon />}
          >
            Guardar y salir
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AuditExecutor;
